import { ButtonType, ButtonWidget, ToastType, ToastWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import TagHttpClient from '~src/httpClient/TAG/TagHttpClient';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { SettingsStyles } from '~src/settings/styles/CommonStyles';

import { openRemoveDomainDialog } from '../helpers/CustomDomainDialogUtility';
import { DeleteCustomDomainTagRequest, IDeleteCustomDomainSettingResult } from '../serverAPI/requests/DeleteCustomDomainTagRequest';
import { GetCustomDomainSettingTagRequest, ICustomDomainSettingResult } from '../serverAPI/requests/GetCustomDomainSettingTagRequest';
import { CustomDomainSettingResponse, Status } from '../serverAPI/types/CustomDomainTypes';
import { AddCustomDomainView } from './AddCustomDomainView';
import { ConfiguredCustomDomainView } from './ConfiguredCustomDomainView';
import { UploadSSLContainerView } from './UploadSSLContainerView';
import { ValidateCnameView } from './ValidateCnameView';

export interface CustomDomainSettingState {
  showAddCustomDomainView: boolean;
  customDomainSetting: CustomDomainSettingResponse;
  isUploading: boolean;
}

export class CustomDomainSettingView extends React.PureComponent<{}, CustomDomainSettingState> {
    public constructor(props: {}) {
        super(props);
        this.state = {
          showAddCustomDomainView: true,
          isUploading: false,
          customDomainSetting: {
            site_luid: '',
            custom_domain: '',
            intermediate_domain: '',
            status: '',
            request_initiation_date: undefined,
            tls_certificate_uploaded_date: undefined,
            tls_certificate_expiry_date: undefined
          }
        };
      }

    private renderSetupCustomDomainView() {
        return  <>
        <div style={{position: 'relative'}}>
          <ButtonWidget buttonType={ButtonType.Destructive}
          testId='cancel-custom-domain-on-site-button'
          showSpinner={this.state.isUploading}
          onClick ={() => this.handleCancelClick()}>
            {Messages.cancel()}
          </ButtonWidget>
        </div>

        <div className={SettingsStyles.settingsGroupDescription}>
          <AddCustomDomainView customDomainSetting={this.state.customDomainSetting} triggerFetchCustomDomain={() => this.fetchAndRefreshCustomDomainSetting()}/>
          <ValidateCnameView  customDomainSetting={this.state.customDomainSetting} triggerFetchCustomDomain={() => this.fetchAndRefreshCustomDomainSetting()}/>
          <UploadSSLContainerView  customDomainSetting={this.state.customDomainSetting} triggerFetchCustomDomain={() => this.fetchAndRefreshCustomDomainSetting()}/>
        </div>
      </>;
    }

    /**
     * If intermediate domain is created, it indicates that Custom Domain creation process has started. If cancel button is clicked, then call the delete API.
     * If Custom Domain creation has not yet started, then just toggle back to the default view.
     */
    private handleCancelClick() {
      if(this.state.customDomainSetting?.intermediate_domain) {
        openRemoveDomainDialog(() => this.deleteCustomDomainSetting());
      }
      this.setState({showAddCustomDomainView: true});
    }

    private deleteCustomDomainSetting() {
      this.setState({isUploading: true});
      TagHttpClient.sendTagRequest(new DeleteCustomDomainTagRequest({site_luid :this.getSiteLuid()})).then(async (res: IDeleteCustomDomainSettingResult)  => {
        this.fetchAndRefreshCustomDomainSetting();
      }).catch(err => {
        ToastWidget.show({message: Messages.CustomDomainSetting_deleteDomainFailedToast(), testId: 'toast-delete-custom-domain-failed', toastType: ToastType.Error});
      }).finally(() => this.setState({isUploading: false}));
    }

    public componentDidMount() {
      this.fetchAndRefreshCustomDomainSetting();
    }

    private fetchAndRefreshCustomDomainSetting() {
      TagHttpClient.sendTagRequest(new GetCustomDomainSettingTagRequest({site_luid :this.getSiteLuid()})).then((res: ICustomDomainSettingResult)  => {
          this.setCustomDomain(res);
        }).catch(err => {
          ToastWidget.show({message: Messages.CustomDomainSetting_getDomainFailedToast(), testId: 'toast-get-custom-domain-failed', toastType: ToastType.Error});
        });
    }

    private getSiteLuid() {
      return getSessionManagementServiceInstance().sessionSiteLuid();
    }

    private setCustomDomain(res: ICustomDomainSettingResult) {
      const customDomainSettingResponse ={...this.state.customDomainSetting,
        site_luid: res.site_luid,
        custom_domain: res.custom_domain,
        intermediate_domain: res.intermediate_domain,
        status: res.status,
        request_initiation_date: res.request_initiation_date,
        tls_certificate_uploaded_date: res.tls_certificate_uploaded_date,
        tls_certificate_expiry_date: res.tls_certificate_expiry_date};

      this.setState({customDomainSetting: customDomainSettingResponse});
    }

    private shouldDisplayConfiguredView(): boolean {
      return  this.state.customDomainSetting?.status === Status.customDomainProvisioned || this.state.customDomainSetting?.status === Status.provisioning ||
              this.state.customDomainSetting?.status === Status.deleting || this.state.customDomainSetting?.status === Status.deletionFailed ||
              this.state.customDomainSetting?.status === Status.provisioningFailed;
    }

    public render() {
        return <>
        {  this.shouldDisplayConfiguredView() ?
          <ConfiguredCustomDomainView customDomainSetting={this.state.customDomainSetting} triggerFetchCustomDomain={() => this.fetchAndRefreshCustomDomainSetting()}/> :
        <div>
          { (this.state.customDomainSetting?.intermediate_domain || !this.state.showAddCustomDomainView) ?
          this.renderSetupCustomDomainView():
          <ButtonWidget buttonType={ButtonType.Outline}
          testId='add-custom-domain-on-site-button'
          className='tb-outline-button'
          onClick={() => this.setState({showAddCustomDomainView: false})}>
              {Messages.CustomDomainSetting_addButton()}
          </ButtonWidget>  }
        </div>
      }
      </>;
    }
}